import React, { useEffect } from 'react'
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { useSquareCheckout } from '../Square/Hooks/useSquareCheckout'
import crashlytics from '../Crashlytics/crashlyticsLog'
import { useAppDispatch, useAppSelector } from '../../state/hooks'
import { setCheckoutState } from '../../state/Slices/checkoutSlice'
import { ButtonContainer } from '../../screens/Checkout/Modals/PaymentMethods/ButtonContainer'
import { showAlert } from '../../state/Slices/settingsSlice'
import { auth } from '../../firebase/config'
import { captureEvent, AnalyticsEvent, getRestaurantMap } from '../../helpers/analytics'
import { getStripeErrorMessage } from '../../utils/getStripeErrorMessage'
import useInjectOrderParams from '../../hooks/useInjectOrderParams'

export default function StripeWebPayment(props) {
	const {
		onPress,
		saveCard,
		amount,
		userStripeId,
		cardId,
		userId,
		toggleModal,
		prices,
		paymentProvider,
		paymentRequest,
		handleBack,
		isDelivery,
		data: checkoutData,
	} = props

	const stripe = useStripe()
	const elements = useElements()
	const functions = getFunctions()

	const dispatch = useAppDispatch()
	const { chargeCustomerCard } = useSquareCheckout()
	const { userLocation } = useAppSelector(state => state.user)
	const { loadedData, restaurants } = useAppSelector(state => state.data)
	const { isTestMode, cart, couponCode } = useAppSelector(state => state.persisted)
	const { giftCard, products, takeOutName, selectedMethods } = useAppSelector(
		state => state.checkout
	)
	const { injectParameters } = useInjectOrderParams(checkoutData)
	const handleSuccessfulPayment = async (processedOrder, payIntentId) => {
		try {
			await injectParameters(processedOrder.orderId)
		} catch (error) {
			console.log('Error with injectParameters:', error)
			return
		}

		try {
			await successfulPayment(processedOrder, payIntentId)
		} catch (error) {
			console.log('Error with successfulPayment:', error)
		}
	}
	useEffect(() => {
		if (paymentRequest && (cardId === 'googlePay' || cardId === 'applePay')) {
			const handlePaymentMethod = async event => {
				if (event) {
					await handleSubmit(event)
				}
			}
			paymentRequest.on('paymentmethod', handlePaymentMethod)
			return () => paymentRequest.off('paymentmethod', handlePaymentMethod)
		}
	}, [paymentRequest, cardId])

	const handleSubmit = async event => {
		// We don't want to let default form submission happen here,
		// which would refresh the page.

		if (!event.paymentMethod) {
			event.preventDefault()
		}

		if (!stripe || !elements) {
			// Stripe.js has not yet loaded.
			// Make sure to disable form submission until Stripe.js has loaded.
			return
		}

		if (paymentProvider === 'stripe' && amount >= 0.005 && amount < 0.5) {
			dispatch(setCheckoutState({ spinner: false, orderPlaced: false }))
			toggleModal(true)
			dispatch(
				showAlert({
					title: 'Minimum order of $0.50 required',
					body: '',
				})
			)
			return
		}

		//submit order to square
		const processedOrder = await onPress()
		if (processedOrder && processedOrder.data) {
			dispatch(setCheckoutState({ processedOrder: processedOrder }))
			const fbOrderId = processedOrder.orderId ?? processedOrder.data?.orderId
			if (amount < 0.005 && processedOrder && processedOrder !== 'PaymentError') {
				console.log('Order total is $0')
				await handleSuccessfulPayment(processedOrder, null)
			} else if (processedOrder && processedOrder !== 'PaymentError') {
				try {
					const payIntent: any = await createPaymentIntent(fbOrderId, event)
					const secret = payIntent.data.secret ? payIntent.data.secret.toString() : ''
					const payIntentId = payIntent.data.id ? payIntent.data.id.toString() : ''

					if (secret === '' || payIntentId === '') {
						dispatch(setCheckoutState({ spinner: false, orderPlaced: false }))
						toggleModal(true)
						dispatch(
							showAlert({
								title:
									secret === '' ? 'Stripe Pay Intent Secret is empty ' : 'Pay Intent ID is empty',
								body: 'Please try again',
							})
						)
						return
					} else if (cardId === 'googlePay' || cardId === 'applePay') {
						// Handle digital wallet payments
						if (event.paymentMethod) {
							event.complete('success')
							const result = await stripe.confirmCardPayment(secret, {
								payment_method: event.paymentMethod.id,
							})

							if (result.paymentIntent?.status === 'succeeded') {
								await handleSuccessfulPayment(processedOrder, payIntentId)
							} else {
								console.log('Payment confirmation error', result.error)
								dispatch(
									showAlert({
										title: 'Payment Error',
										body: getStripeErrorMessage(result.error.code, result.error.message),
									})
								)
							}
						}
					} else if (cardId === '') {
						try {
							const { error } = await stripe.confirmCardPayment(secret, {
								payment_method: {
									card: elements.getElement(CardElement),
								},
							})

							if (error) throw error

							console.log('Success from promise')
							await handleSuccessfulPayment(processedOrder, payIntentId)
						} catch (error) {
							dispatch(setCheckoutState({ spinner: false, orderPlaced: false }))
							toggleModal(true)
							if (error.message) {
								crashlytics().log(error.message)
							}
							dispatch(
								showAlert({
									title: 'Payment Error',
									body: getStripeErrorMessage(error.code, error.message),
								})
							)
						}
					} else {
						await handleSuccessfulPayment(processedOrder, payIntentId)
					}
				} catch (error) {
					dispatch(setCheckoutState({ spinner: false, orderPlaced: false }))
					toggleModal(true)

					dispatch(
						showAlert({
							title: 'Payment Error',
							body: getStripeErrorMessage(error.code, error.message),
						})
					)
				}
			}
		}
	}

	async function createPaymentIntent(orderId, event) {
		const createStripePaymentIntent = httpsCallable(functions, 'v2_createStripePaymentIntent')
		return await createStripePaymentIntent({
			amount: amount.toFixed(2) * 100,
			firebaseOrganizationId: global.org,
			firebaseOrderId: orderId,
			stripeCustomerId: userStripeId,
			saveCard: cardId === 'applePay' || cardId === 'googlePay' ? false : saveCard,
			cardId:
				cardId === 'applePay' || cardId === 'googlePay'
					? ''
					: event.paymentMethod
						? event.paymentMethod.id
						: cardId,
			terminalPayment: false,
			prices: prices,
			isTest: isTestMode,
		})
	}

	async function successfulPayment(processedOrder, payIntentId) {
		crashlytics().log('SETTING ORDER PLACED: TRUE')
		console.log('Success')
		dispatch(setCheckoutState({ orderPlaced: true }))
		const updateOrderInDB = httpsCallable(functions, 'v2_updateOrderInDB')
		const updateOrderInDBTest = httpsCallable(functions, 'v2_updateOrderInDBTest')
		const sendEmailReceiptTest = httpsCallable(functions, 'v2_sendEmailReceiptTest')
		const sendGiftCardText = httpsCallable(functions, 'v2_sendTextGiftCard')

		if (processedOrder.isProductOrder) {
			await updateOrderInDBTest({
				orgId: global.org,
				orderId: processedOrder.data.orderId,
				orderObj: {
					status: 'Completed',
				},
				productActivation: {
					productType: 'giftCard',
					amount: processedOrder.data.productDetails.amount,
					id: processedOrder.data.productId,
				},
			})
			const product = products[0]
			await sendEmailReceiptTest({
				senderName:
					auth.currentUser && !global.kiosk
						? auth.currentUser.displayName
						: (product.senderName ?? takeOutName),
				recipientName: product.recipient?.name,
				userEmail:
					product.recipientType == 'personal'
						? selectedMethods.emailString
						: product.recipient?.email,
				amount: product.amount,
				productId: processedOrder.data.productId,
				giftNote: product.customMessage,
				orgId: global.org,
				orgName: loadedData[global.org].name,
				orgLogo: loadedData[global.org].logoDark ?? loadedData[global.org].logo,
				emailLogo: loadedData[global.org].emailLogo,
				orgWebURL: loadedData[global.org].webURL,
				type: 'giftCard',
			})
			await sendGiftCardText({
				senderName:
					auth.currentUser && !global.kiosk
						? auth.currentUser.displayName
						: (product.senderName ?? takeOutName),
				recipientName: product.recipient?.name,
				userPhone:
					product.recipientType == 'personal'
						? selectedMethods.phoneString
						: product.recipient.phoneNumber,
				amount: product.amount,
				productId: processedOrder.data.productId,
				giftNote: product.customMessage,
				orgId: global.org,
				orgName: loadedData[global.org].name,
				orgLogo: loadedData[global.org].logoDark ?? loadedData[global.org].logo,
				orgWebURL: loadedData[global.org].webURL,
				emailLogo: loadedData[global.org].emailLogo,
			})
			return
		}

		const order = processedOrder.data
		const orderId = processedOrder.orderId

		try {
			console.log('ORDER ID ' + orderId)
			if (order) {
				dispatch(
					setCheckoutState({
						processedOrder: order,
						orderId,
						orderNumber: processedOrder.data?.orderNumber ?? processedOrder.orderNumDisplay,
					})
				)
			}

			console.log(processedOrder)

			const restaurantData = getRestaurantMap(processedOrder.data, restaurants)
			const { taxes } = prices
			captureEvent(AnalyticsEvent.Purchase, {
				userId,
				cart,
				orderId,
				restaurantData,
				couponCode,
				taxes,
			})

			// 2024/01/28: v2_ProcessPayment currently performs the following: updateOrderStatus, redeemUserCoupon, createDeliverectOrder.
			const processPaymentForSquare = httpsCallable(functions, 'v2_processPayment')
			await processPaymentForSquare({
				orgId: global.org,
				orderData: order,
				uid: userId,
				paymentIntentId: payIntentId,
				isKiosk: false,
				isDineIn: false,
				address: userLocation && isDelivery ? userLocation.addressObject : null,
				orderId,
				sendOrdersToDeliverect: loadedData[global.org].sendOrdersToDeliverect ?? true,
			})
		} catch (e) {
			console.log('Error with Square payment, updating order status')
			await updateOrderInDB({
				orgId: global.org,
				orderId,
				orderObj: { orderId, status: global.kiosk ? 'In Progress' : 'Accepted' },
			})
		} finally {
			if (giftCard) {
				await updateOrderInDBTest({
					orgId: global.org,
					orderId,
					orderObj: {},
					giftCardUsage: {
						orderId,
						code: giftCard.code,
						amount: prices.redeemedGiftCard * 100,
						date: new Date()?.toISOString?.(),
						type: 'redeem',
					},
				})
			}
		}
	}

	const showSquareCard = () => {
		toggleModal()
		dispatch(setCheckoutState({ showSquarePayment: true, cardElementVisible: true }))
	}

	const handleSquareCustomerCardCharge = async () => {
		const paymentResponse = await chargeCustomerCard()
		const processedOrder = paymentResponse.processedOrder
		if (processedOrder && processedOrder.data && processedOrder !== 'PaymentError') {
			const fbOrderId = processedOrder.orderId
			if (paymentResponse.status === 'Failed') {
				dispatch(setCheckoutState({ spinner: false, orderPlaced: false }))
				toggleModal(true)
				const updateOrderInDB = httpsCallable(functions, 'v2_updateOrderInDB')
				const statusObj = { status: 'Failed' }
				updateOrderInDB({
					orgId: global.org,
					orderId: fbOrderId,
					orderObj: statusObj,
				}).then(() => {
					console.log('Status updated to ' + statusObj.status)
				})
			}
		}
		crashlytics().log('SETTING ORDER PLACED: TRUE')
		dispatch(setCheckoutState({ orderPlaced: true }))
	}
	const handleDigitalWalletPay = () => {
		paymentRequest.show()
	}

	useEffect(() => {
		if (paymentProvider === 'stripe' && stripe) {
			const pr = stripe.paymentRequest({
				country: 'CA',
				currency: 'cad',
				total: {
					label: 'Total owed:',
					amount: Math.round(amount.toFixed(2) * 100),
				},
				requestPayerName: true,
				requestPayerEmail: true,
				disableWallets: ['link'],
			})

			// Check the availability of the Payment Request API.
			pr.canMakePayment()
				.then(result => {
					if (result) {
						dispatch(setCheckoutState({ paymentRequest: pr }))
					}
				})
				.catch(error => {
					console.log(error)
				})
		}
	}, [stripe])

	return (
		<>
			<ButtonContainer
				handleBack={handleBack}
				toggleModal={
					paymentRequest && (cardId === 'googlePay' || cardId === 'applePay')
						? handleDigitalWalletPay
						: paymentProvider === 'square'
							? cardId === ''
								? showSquareCard
								: handleSquareCustomerCardCharge
							: handleSubmit
				}
			/>
		</>
	)
}
